/**
 * Questo file definisce la pagina Home dell'applicazione.
 * - È la landing page, la prima pagina che un visitatore vede.
 * - È strutturata in diverse sezioni per presentare l'applicazione:
 * 1. Hero Section: una grande intestazione con un titolo accattivante e una call-to-action.
 * 2. Features Section: descrive le funzionalità principali.
 * 3. How It Works Section: spiega i passaggi per usare l'app.
 * 4. Testimonials Section: mostra recensioni fittizie di utenti.
 * 5. FAQ Section: risponde alle domande più comuni.
 * 6. CTA Section: un'ultima call-to-action per incoraggiare la registrazione.
 * 7. Footer: contiene link e informazioni di contatto.
 * - Il componente è suddiviso in sub-componenti più piccoli e riutilizzabili
 * (FeatureCard, HowItWorksStep, ecc.) per mantenere il codice pulito e organizzato.
 * - Utilizza il componente `Navbar`.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import './Home.css';

// --- SUB-COMPONENTI PER LA PAGINA HOME ---
// Suddividere la UI in componenti più piccoli migliora la leggibilità e la manutenibilità.

/**
 * Sub-componente per le card delle funzionalità.
 * @param {{icon: React.ReactNode, title: string, text: string}} props
 */
const FeatureCard = ({ icon, title, text }) => (
  <div className="col-lg-3 col-md-6 mb-4">
    <div className="feature-card h-100 text-center p-4">
      <div className="feature-icon mb-3">{icon}</div>
      <h5 className="fw-bold">{title}</h5>
      <p className="mb-0 text-muted">{text}</p>
    </div>
  </div>
);

/**
 * Sub-componente per i passaggi della sezione "Come funziona".
 * @param {{number: string, title: string, description: string}} props
 */
const HowItWorksStep = ({ number, title, description }) => (
    <div className="col-md-3">
        <div className="how-step text-center p-4 h-100">
            <div className="step-number mb-3">{number}</div>
            <h6 className="fw-bold">{title}</h6>
            <p className="mb-0 text-muted">{description}</p>
        </div>
    </div>
);

/**
 * Sub-componente per le card delle testimonianze.
 * @param {{quote: string, author: string, role: string, image: string}} props
 */
const TestimonialCard = ({ quote, author, role, image }) => (
    <div className="col-lg-4 mb-4">
        <div className="testimonial-card h-100">
            <div className="testimonial-body">
                <i className="bi bi-quote testimonial-quote-icon"></i>
                <p className="testimonial-text">"{quote}"</p>
            </div>
            <div className="testimonial-footer">
                <img src={image} alt={`Foto di ${author}`} className="testimonial-image" />
                <div>
                    <div className="testimonial-author">{author}</div>
                    <div className="testimonial-role">{role}</div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * Sub-componente per un singolo elemento della FAQ (accordion).
 * @param {{id: string, question: string, answer: string}} props
 */
const FaqItem = ({ id, question, answer }) => (
    <div className="accordion-item">
        <h2 className="accordion-header" id={`heading-${id}`}>
            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#collapse-${id}`}>
                {question}
            </button>
        </h2>
        <div id={`collapse-${id}`} className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
            <div className="accordion-body">{answer}</div>
        </div>
    </div>
);

/**
 * Componente principale della pagina Home.
 */
export default function Home() {
  // Funzione helper per prevenire il comportamento di default dei link vuoti nel footer.
  const preventDefault = (e) => e.preventDefault();

  return (
    <div className="home-page">
      <Navbar />

      {/* Sezione Hero */}
      <header className="hero-section d-flex align-items-center">
        <div className="container">
            <div className="row align-items-center">
                <div className="col-lg-6 text-center text-lg-start">
                    <h1 className="display-2 fw-bolder mb-3 text-white">Dai vita alle tue idee</h1>
                    <p className="lead mb-4 text-white-75">
                        La piattaforma intelligente che trasforma i tuoi documenti in flashcard interattive. Studia in modo più efficace, organizza le tue conoscenze e condividile con chi vuoi.
                    </p>
                    <div>
                        <Link to="/register" className="btn btn-primary btn-lg me-2 px-4 shadow-lg">Inizia Subito, è Gratis</Link>
                    </div>
                </div>
                <div className="col-lg-6 d-none d-lg-block">
                    {/* Immagine placeholder per illustrare il concetto dell'app. */}
                    <img src="https://placehold.co/600x400/ffffff/4A90E2?text=DOMANDA:%0APronto+a+studiare%0Ain+modo+smart%3F" className="img-fluid hero-image" alt="Illustrazione motivazionale" />
                </div>
            </div>
        </div>
      </header>

      {/* Sezione Funzionalità */}
      <section id="features" className="features-section py-5">
        <div className="container py-4">
          <h2 className="text-center mb-5 display-5 fw-bold">Funzionalità Principali</h2>
          <div className="row justify-content-center">
            <FeatureCard icon={<i className="bi bi-file-earmark-arrow-up-fill fs-1"></i>} title="Upload Intelligente" text="Carica file PDF, Word, PPTX o TXT. Il nostro sistema estrae il testo per te." />
            <FeatureCard icon={<i className="bi bi-stars fs-1"></i>} title="Generazione AI" text="Crea flashcard automaticamente dai tuoi documenti usando la potenza di OpenAI." />
            <FeatureCard icon={<i className="bi bi-folder2-open fs-1"></i>} title="Organizzazione" text="Gestisci le tue flashcard in cartelle personali, facili da consultare e modificare." />
            <FeatureCard icon={<i className="bi bi-share-fill fs-1"></i>} title="Condivisione Facile" text="Rendi pubbliche le tue cartelle di studio per condividerle con altri utenti." />
          </div>
        </div>
      </section>

      {/* Sezione Come Funziona */}
      <section className="how-it-works-section py-5">
          <div className="container py-4">
              <h2 className="text-center mb-5 display-5 fw-bold">Semplice come contare fino a 4</h2>
              <div className="row gy-4 justify-content-center">
                  <HowItWorksStep number="1" title="Registrati" description="Crea il tuo account gratuito in meno di un minuto." />
                  <HowItWorksStep number="2" title="Carica un File" description="Fai l'upload di un documento che vuoi studiare." />
                  <HowItWorksStep number="3" title="Genera Flashcard" description="Lascia che l'AI crei le flashcard per te in pochi istanti." />
                  <HowItWorksStep number="4" title="Studia e Condividi" description="Inizia a studiare con le tue nuove card e condividile." />
              </div>
          </div>
      </section>

      {/* Sezione Testimonianze */}
      <section id="testimonials" className="testimonials-section py-5">
        <div className="container py-4">
            <h2 className="text-center mb-5 display-5 fw-bold">Dicono di noi</h2>
            <div className="row">
                <TestimonialCard quote="FlashMind ha trasformato il mio modo di preparare gli esami..." author="Giulia Rossi" role="Studentessa di Giurisprudenza" image="https://placehold.co/100x100/E8F0FE/333?text=GR" />
                <TestimonialCard quote="Incredibilmente utile per creare materiale didattico..." author="Marco Bianchi" role="Insegnante di Liceo" image="https://placehold.co/100x100/E8F0FE/333?text=MB" />
                <TestimonialCard quote="La uso per memorizzare concetti tecnici e per la formazione continua..." author="Luca Verdi" role="Sviluppatore Software" image="https://placehold.co/100x100/E8F0FE/333?text=LV" />
            </div>
        </div>
      </section>

      {/* Sezione FAQ */}
      <section id="faq" className="faq-section py-5">
        <div className="container py-4">
            <h2 className="text-center mb-5 display-5 fw-bold">Domande Frequenti</h2>
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <div className="accordion" id="faqAccordion">
                        <FaqItem id="one" question="FlashMind è gratuito?" answer="Sì, la registrazione e le funzionalità di base sono gratuite..." />
                        <FaqItem id="two" question="Quali tipi di file sono supportati?" answer="Attualmente supportiamo file in formato PDF, DOCX, PPTX e TXT..." />
                        <FaqItem id="three" question="La mia API key di OpenAI è al sicuro?" answer="Assolutamente sì. La tua API key viene criptata..." />
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Sezione Call to Action (CTA) */}
      <section className="cta-section py-5 text-center text-white">
        <div className="container py-4">
          <h2 className="display-4 fw-bold">Pronto a rivoluzionare il tuo studio?</h2>
          <p className="lead my-4">Entra a far parte della nostra community. È gratis!</p>
          <Link to="/register" className="btn btn-light btn-lg fw-bold px-5 py-3 shadow-lg">Registrati Ora</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-section py-5">
        <div className="container">
            <div className="row">
                <div className="col-lg-4 mb-4 mb-lg-0">
                    <h5 className="footer-heading">FlashMind</h5>
                    <p className="text-white-50">Trasforma il modo in cui studi e impari, una flashcard alla volta.</p>
                </div>
                <div className="col-lg-2 col-md-4 col-6 mb-4 mb-lg-0">
                    <h5 className="footer-heading">Prodotto</h5>
                    <ul className="list-unstyled footer-links">
                        <li><a href="#features">Funzionalità</a></li>
                        <li><a href="#faq">Sicurezza</a></li>
                    </ul>
                </div>
                <div className="col-lg-2 col-md-4 col-6 mb-4 mb-lg-0">
                    <h5 className="footer-heading">Community</h5>
                    <ul className="list-unstyled footer-links">
                        <li><Link to="/public">Flashcard Pubbliche</Link></li>
                        <li><a href="#testimonials">Testimonianze</a></li>
                    </ul>
                </div>
                <div className="col-lg-4 col-md-4 text-md-end">
                    <h5 className="footer-heading">Seguici</h5>
                    <div className="social-icons">
                        <a href="/#" onClick={preventDefault}><i className="bi bi-twitter-x"></i></a>
                        <a href="/#" onClick={preventDefault}><i className="bi bi-facebook"></i></a>
                        <a href="/#" onClick={preventDefault}><i className="bi bi-instagram"></i></a>
                    </div>
                </div>
            </div>
            <hr className="my-4" />
            <div className="text-center text-white-50">
                <p className="mb-0">&copy; {new Date().getFullYear()} FlashMind. Tutti i diritti riservati.</p>
            </div>
        </div>
      </footer>
    </div>
  );
}